import { VisibilityOff, Visibility } from '@mui/icons-material';
import {
  Box,
  InputAdornment,
  IconButton,
  Alert,
  Link,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { updateSignInFormField, resetCreatedOrg } from '../../features/user/userSlice';
import { useNavigate } from 'react-router-dom';
import {
  authenticateUser,
  checkRequestJoinOrgStatus,
  matchOrg,
  resendVerificationCode,
} from '../../features/user/utils';
import StyledBox from './styled/SignIn/StyledBox';
import CheckIcon from '@mui/icons-material/Check';
import { Link as RouterLink } from 'react-router-dom';
import { TextField } from '../new/inputs/TextField';
import { Button } from '../new/Button';
import { Typography } from '../new/Typography';

const SignIn = () => {
  const navigate = useNavigate();

  const dispatch: AppDispatch = useDispatch();

  const formData = useSelector((state: RootState) => state.user.signInForm);

  const isAuthenticating = useSelector((state: RootState) => state.user.isAuthenticating);

  const hasCreatedOrganization = useSelector((state: RootState) => state.user.hasCreatedOrganization);

  const isFromInvToken = useSelector((state: RootState) => state.user.isFromInvToken);

  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    dispatch(authenticateUser({ formType: 'signInForm' })).then((result) => {
      if (authenticateUser.fulfilled.match(result)) {
        const hasJoinedOrganization =
          result.payload.joiningStatus === 'joined' || result.payload.teamRole === 'support';
        const accessToken = result.payload.accessToken;

        if (hasJoinedOrganization) {
          navigate('/');
        } else {
          dispatch(checkRequestJoinOrgStatus(accessToken)).then((result) => {
            const reqStatus = result.payload.status;

            if (reqStatus === 'pending') {
              navigate('/auth/pending', { state: { isProgrammatic: true } });
            }
          });
        }
      } else if (authenticateUser.rejected.match(result)) {
        const errorType = result.payload as {
          clientCode: string;
          payload: { signupToken: string };
        };

        if (errorType.clientCode === 'NOT_JOINED_ORGANIZATION') {
          dispatch(matchOrg()).then((result) => {
            navigate('/auth/organization');
          });
        }

        if (errorType.clientCode === 'EMAIL_NOT_VERIFIED') {
          dispatch(resendVerificationCode('signin'));
          navigate('/auth/sign-up', { state: { fromSignIn: true } });
        }
      }
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const navigateToRegister = () => {
    navigate('/auth/sign-up');
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(resetCreatedOrg());
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [hasCreatedOrganization]);

  return (
    <StyledBox>
      {hasCreatedOrganization && (
        <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
          Organization successfully created. You may now sign in.
        </Alert>
      )}
      {isFromInvToken && (
        <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
          Successfully joined organization. You may now sign in.
        </Alert>
      )}
      <Typography variant="h5" sx={{ fontSize: '1.875rem' }}>
        Welcome!
      </Typography>
      <Box component="form" noValidate sx={{ mt: 4, width: '50%' }}>
        <TextField
          required
          label="Email Address"
          id="email"
          placeholder="Add..."
          name="email"
          autoFocus
          autoComplete="email"
          value={formData.email}
          onChange={(e) =>
            dispatch(
              updateSignInFormField({
                fieldName: 'email',
                value: e.target.value,
              }),
            )
          }
        />
        <TextField
          required
          label="Password"
          id="password"
          placeholder="Add..."
          name="password"
          value={formData.password}
          type={showPassword ? 'text' : 'password'}
          onChange={(e) =>
            dispatch(
              updateSignInFormField({
                fieldName: 'password',
                value: e.target.value,
              }),
            )
          }
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
                sx={{mr: 0.5}}
              >
                {showPassword ? <VisibilityOff sx={{ color: '#000' }} /> : <Visibility sx={{ color: '#000' }} />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Link component={RouterLink} to="/auth/reset" variant="body2" sx={{ display: 'block', textAlign: 'right', mt: -2, fontWeight: 600 }}>
          Reset Password
        </Link>
        <Button
          type="primary"
          buttonType='submit'
          disabled={isAuthenticating}
          sx={{ mt: 3, mb: 2, borderRadius: '0px', textTransform: 'initial' }}
          onClick={handleSubmit}
        >
          {isAuthenticating ? 'Verifying credentials...' : 'Sign in'}
        </Button>
        <Button
          type='secondary'
          onClick={navigateToRegister}
          sx={{
            mb: 3,
            background: '#ED870C',
            border: '0.0625rem solid #ED870C',
            color: '#FFFFFF',
            '&:hover': {
              background: '#ED870C',
            },
          }}
        >
          Don't have an account? Create one!
        </Button>
        {!!formData.error && (
          <Alert severity="error">
            We do not recognize this email password combination. Try again or reset your password
          </Alert>
        )}
      </Box>
    </StyledBox>
  );
};

export default SignIn;
